<script lang="ts">
// UMA dica aberta por vez em toda a tela. Duas abertas se sobrepõem — elas são
// absolutas e vazam por cima do bloco de baixo — e o clique que abre a segunda
// não chega na primeira pra fechá-la (o ⓘ para a propagação, senão pedir ajuda
// recolheria o bloco).
//
// Mora NESTE <script>, e não no <script setup>: o corpo do setup roda uma vez
// POR INSTÂNCIA, então lá isto viraria um ref por bloco e cada dica seguiria
// ignorando as outras. Aqui é módulo de verdade, avaliado uma vez só.
const openHelpId = ref<string | null>(null);
</script>

<script setup lang="ts">
type Accent = 'cyan' | 'purple' | 'pink' | 'good';

type Props = {
  title: string;
  /** Linha miúda sob o título. Continua visível com o bloco fechado — é ela que
   *  diz o que tem lá dentro sem precisar abrir. */
  summary?: string;
  /** Texto do ⓘ: como LER a seção, em linguagem de jogador. */
  help?: string;
  accent?: Accent;
  defaultOpen?: boolean;
};

const {
  title,
  summary = '',
  help = '',
  accent = 'cyan',
  defaultOpen = true,
} = defineProps<Props>();

const isOpen = ref(defaultOpen);

const uid = useId();
const bodyId = computed(() => `crash-block-body-${uid}`);
const helpId = computed(() => `crash-block-help-${uid}`);

const isHelpOpen = computed(() => openHelpId.value === uid);

// O ⓘ vive DENTRO da linha que abre e fecha o bloco: sem parar a propagação,
// pedir ajuda também recolheria a seção que você quer ler.
function toggleHelp(event: Event) {
  event.stopPropagation();
  openHelpId.value = isHelpOpen.value ? null : uid;
}

function closeHelp() {
  if (isHelpOpen.value) openHelpId.value = null;
}

// Clique em qualquer outro lugar fecha a dica. O clique do próprio ⓘ nunca
// chega aqui — ele para a propagação —, então não há corrida entre abrir e
// fechar no mesmo evento.
function onDocumentClick() {
  closeHelp();
}

function onDocumentKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') closeHelp();
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onDocumentKeydown);
});

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onDocumentKeydown);
  closeHelp();
});
</script>

<template>
  <section
    class="crash-block"
    :class="[`crash-block--${accent}`, { 'is-closed': !isOpen }]"
    data-crash-block
  >
    <!-- A linha inteira é o alvo: num sheet mobile, mirar só o chevron erra. -->
    <div
      class="crash-block__top"
      @click="isOpen = !isOpen"
    >
      <h3 class="crash-block__h">
        <button
          :aria-controls="bodyId"
          :aria-expanded="isOpen"
          class="crash-block__head"
          type="button"
        >
          <span
            aria-hidden="true"
            class="crash-block__mark"
          >
            <slot name="icon" />
          </span>

          <span class="crash-block__title">
            {{ title }}
            <span
              v-if="summary"
              class="crash-block__summary"
            >
              {{ summary }}
            </span>
          </span>
        </button>
      </h3>

      <button
        v-if="help"
        :aria-controls="helpId"
        :aria-expanded="isHelpOpen"
        aria-label="Como ler esta seção"
        class="crash-block__help-btn"
        type="button"
        @click="toggleHelp"
      >
        <svg
          aria-hidden="true"
          fill="none"
          height="16"
          viewBox="0 0 16 16"
          width="16"
        >
          <path
            clip-rule="evenodd"
            d="M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM9 5.5C9 6.05228 8.55228 6.5 8 6.5C7.44772 6.5 7 6.05228 7 5.5C7 4.94772 7.44772 4.5 8 4.5C8.55228 4.5 9 4.94772 9 5.5ZM8 7.75C8.41421 7.75 8.75 8.08579 8.75 8.5V11C8.75 11.4142 8.41421 11.75 8 11.75C7.58579 11.75 7.25 11.4142 7.25 11V8.5C7.25 8.08579 7.58579 7.75 8 7.75Z"
            fill="currentColor"
            fill-rule="evenodd"
          />
        </svg>
      </button>

      <span
        aria-hidden="true"
        class="crash-block__chev"
      >
        <svg
          fill="none"
          height="16"
          viewBox="0 0 16 16"
          width="16"
        >
          <path
            clip-rule="evenodd"
            d="M2.96967 5.46967C3.26256 5.17678 3.73744 5.17678 4.03033 5.46967L8 9.43934L11.9697 5.46967C12.2626 5.17678 12.7374 5.17678 13.0303 5.46967C13.3232 5.76256 13.3232 6.23744 13.0303 6.53033L8.53033 11.0303C8.23744 11.3232 7.76256 11.3232 7.46967 11.0303L2.96967 6.53033C2.67678 6.23744 2.67678 5.76256 2.96967 5.46967Z"
            fill="currentColor"
            fill-rule="evenodd"
          />
        </svg>
      </span>

      <div
        v-if="help && isHelpOpen"
        :id="helpId"
        class="crash-block__help"
        role="note"
        @click.stop="closeHelp"
      >
        {{ help }}
      </div>
    </div>

    <!-- v-show e não v-if: o conteúdo fica montado com o bloco fechado, então o
         poller compartilhado continua alimentando quem está escondido e reabrir
         não pisca skeleton. -->
    <div
      v-show="isOpen"
      :id="bodyId"
      class="crash-block__body"
    >
      <div class="crash-block__inner">
        <slot />
      </div>
    </div>
  </section>
</template>

<style scoped>
/* O acento é a identidade da seção: pinta a marca, o filete da borda quando
   fechado e a moldura da dica. */
.crash-block--cyan {
  --acento: #37c6f0;
  --acento-fraco: rgba(55, 198, 240, 0.16);
  --acento-brilho: rgba(55, 198, 240, 0.3);
}

.crash-block--purple {
  --acento: #a560f5;
  --acento-fraco: rgba(165, 96, 245, 0.16);
  --acento-brilho: rgba(165, 96, 245, 0.3);
}

.crash-block--pink {
  --acento: #ec5a98;
  --acento-fraco: rgba(236, 90, 152, 0.16);
  --acento-brilho: rgba(236, 90, 152, 0.3);
}

.crash-block--good {
  --acento: #4ed8a6;
  --acento-fraco: rgba(78, 216, 166, 0.16);
  --acento-brilho: rgba(78, 216, 166, 0.3);
}

.crash-block {
  position: relative;
  margin-top: 10px;
  border: 1px solid #1c2740;
  border-radius: 16px;
  background: #0f1727;
}

.crash-block.is-closed {
  background: transparent;
  box-shadow: inset 3px 0 0 var(--acento);
}

@media (min-width: 1024px) {
  .crash-block {
    margin-top: 12px;
  }
}

.crash-block__top {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 14px;
  border-bottom: 1px solid #161f35;
  cursor: pointer;
}

.crash-block.is-closed .crash-block__top {
  border-bottom: 0;
}

@media (min-width: 1024px) {
  .crash-block__top {
    padding: 16px 18px;
  }
}

.crash-block__h {
  flex: 1;
  min-width: 0;
  margin: 0;
}

.crash-block__head {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: #e9eef9;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
}

.crash-block__mark {
  display: grid;
  place-items: center;
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: var(--acento-fraco);
  color: var(--acento);
  box-shadow: 0 0 14px var(--acento-brilho);
}

.crash-block__mark :deep(svg) {
  display: block;
}

.crash-block__title {
  flex: 1;
  min-width: 0;
}

.crash-block__summary {
  display: block;
  margin-top: 3px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 11px;
  font-weight: 500;
}

.crash-block__help-btn {
  display: grid;
  place-items: center;
  flex: none;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid #1c2740;
  border-radius: 50%;
  background: none;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
}

.crash-block__help-btn[aria-expanded='true'] {
  color: var(--acento);
  border-color: var(--acento);
}

.crash-block__help-btn :deep(svg),
.crash-block__chev :deep(svg) {
  display: block;
}

.crash-block__help {
  position: absolute;
  z-index: 5;
  top: calc(100% - 4px);
  right: 14px;
  left: 14px;
  padding: 12px 14px;
  border: 1px solid var(--acento);
  border-radius: 12px;
  background: #070b14;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.6);
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  /* @keyframes e não `transition`: o main.scss zera transition no app todo. */
  animation: crash-help-in 160ms ease-out both;
}

@media (min-width: 1024px) {
  .crash-block__help {
    right: 18px;
    left: 18px;
  }
}

.crash-block__help::before {
  content: '';
  position: absolute;
  top: -5px;
  right: 58px;
  width: 8px;
  height: 8px;
  border-top: 1px solid var(--acento);
  border-left: 1px solid var(--acento);
  background: #070b14;
  transform: rotate(45deg);
}

@keyframes crash-help-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

/* O chevron deita quando o bloco fecha. Troca de animation-name junto com a
   classe pra animação rodar de novo a cada toque. */
.crash-block__chev {
  display: grid;
  place-items: center;
  flex: none;
  width: 34px;
  height: 34px;
  border: 1px solid #1c2740;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.9);
  animation: crash-chev-open 300ms cubic-bezier(0.22, 0.9, 0.3, 1) both;
}

.crash-block.is-closed .crash-block__chev {
  animation: crash-chev-close 300ms cubic-bezier(0.22, 0.9, 0.3, 1) both;
}

@keyframes crash-chev-open {
  from {
    transform: rotate(-90deg);
  }

  to {
    transform: rotate(0deg);
  }
}

@keyframes crash-chev-close {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(-90deg);
  }
}

.crash-block__body {
  overflow: hidden;
  animation: crash-body-in 320ms cubic-bezier(0.22, 0.9, 0.3, 1) both;
}

.crash-block__inner {
  padding: 14px 14px 18px;
}

@media (min-width: 1024px) {
  .crash-block__inner {
    padding: 16px 18px 20px;
  }
}

@keyframes crash-body-in {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .crash-block__help,
  .crash-block__chev,
  .crash-block.is-closed .crash-block__chev,
  .crash-block__body {
    animation: none;
  }

  .crash-block.is-closed .crash-block__chev {
    transform: rotate(-90deg);
  }
}
</style>
